import React, { useEffect, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { Wallet } from 'lucide-react';

// Owner-configurable Billing behaviour: which payment methods appear at checkout
// and whether item / bill discounts are allowed. Stored additively on
// settings.billing_options and consumed by the Billing screen.
const METHODS = [
  ['CASH', 'Cash'],
  ['UPI', 'UPI'],
  ['CARD', 'Card'],
  ['BANK', 'Bank transfer'],
  ['CREDIT', 'Credit (Udhaar)'],
];

export const DEFAULT_BILLING_OPTIONS = {
  methods: { CASH: true, UPI: true, CARD: true, BANK: true, CREDIT: true },
  allow_item_discount: true,
  allow_bill_discount: true,
};

export function BillingOptionsPanel() {
  const { settings, loadSettings } = useApp();
  const [opts, setOpts] = useState(DEFAULT_BILLING_OPTIONS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const bo = settings?.billing_options;
    if (bo) {
      setOpts({
        methods: { ...DEFAULT_BILLING_OPTIONS.methods, ...(bo.methods || {}) },
        allow_item_discount: bo.allow_item_discount !== false,
        allow_bill_discount: bo.allow_bill_discount !== false,
      });
    }
  }, [settings]);

  const enabledCount = METHODS.filter(([k]) => opts.methods[k] !== false).length;

  const toggleMethod = (k) => {
    const next = { ...opts.methods, [k]: opts.methods[k] === false ? true : false };
    if (!METHODS.some(([m]) => next[m] !== false)) {
      toast.error('Keep at least one payment method enabled.');
      return;
    }
    setOpts({ ...opts, methods: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', { billing_options: opts });
      await loadSettings();
      toast.success('Billing options saved');
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="billing-options-panel">
      <div className="mb-1 flex items-center gap-2"><Wallet className="h-4 w-4" /><p className="text-sm font-semibold">Payment &amp; discount options</p></div>
      <p className="mb-3 text-xs text-muted-foreground">Choose which payment methods appear at checkout and whether discounts can be applied.</p>

      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Payment methods</p>
      <div className="mt-2 space-y-1">
        {METHODS.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5" data-testid={`billopt-method-${k}`}>
            <span className="text-sm font-medium">{label}</span>
            <Switch checked={opts.methods[k] !== false} onCheckedChange={() => toggleMethod(k)} data-testid={`billopt-method-switch-${k}`} />
          </div>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{enabledCount} method{enabledCount !== 1 ? 's' : ''} enabled.</p>

      <p className="mt-4 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Discounts</p>
      <div className="mt-2 space-y-1">
        <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5" data-testid="billopt-item-discount">
          <div>
            <p className="text-sm font-medium">Item discounts</p>
            <p className="text-[11px] text-muted-foreground">Allow a discount on individual line items.</p>
          </div>
          <Switch checked={opts.allow_item_discount} onCheckedChange={(v) => setOpts({ ...opts, allow_item_discount: v })} data-testid="billopt-item-discount-switch" />
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5" data-testid="billopt-bill-discount">
          <div>
            <p className="text-sm font-medium">Bill discount</p>
            <p className="text-[11px] text-muted-foreground">Allow a discount on the whole bill total.</p>
          </div>
          <Switch checked={opts.allow_bill_discount} onCheckedChange={(v) => setOpts({ ...opts, allow_bill_discount: v })} data-testid="billopt-bill-discount-switch" />
        </div>
      </div>

      <Button className="mt-4 h-11 w-full rounded-xl" onClick={save} disabled={saving} data-testid="billopt-save">{saving ? 'Saving\u2026' : 'Save billing options'}</Button>
    </div>
  );
}
